// frontend/src/pages/TransferUpload.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

import { toast } from "react-toastify";
import "../assets/game-upload.css";
import PageContainer from "./PageContainer";
import Loader from "../components/Utils/Loader";
import {
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

function TransferUpload() {
  const [recentFiles, setRecentFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [gender, setGender] = useState("");
  const [dragOver, setDragOver] = useState(false); // Track drag-over state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadType, setUploadType] = useState("players");
  const [folderPath, setFolderPath] = useState("");
  const [processingFolder, setProcessingFolder] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [totalFilesToProcess, setTotalFilesToProcess] = useState(0);
  const [processedFiles, setProcessedFiles] = useState(0);
  const folderInputRef = useRef(null);
  const [season, setSeason] = useState(""); // Add state for season selection
  const [processingXFactor, setProcessingXFactor] = useState(false);

  useEffect(() => {
    const storedFiles = JSON.parse(localStorage.getItem("recentUploads")) || [];
    setRecentFiles(storedFiles);
  }, []);

  const uploadFile = useCallback(
    async (file) => {
      if (!file.name.toLowerCase().endsWith(".csv")) {
        toast.error("Only CSV files are supported!");
        return;
      }

      setUploadingFile(file);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("gender", gender);

      // Add season if selected for player data uploads
      if (season && uploadType === "playerdata") {
        formData.append("season", season);
      }

      // Determine the endpoint based on upload type
      let endpoint;
      switch (uploadType) {
        case "players":
          endpoint = "/api/upload/transfers";
          break;
        case "gamelogs":
          endpoint = "/api/upload/gamelogs";
          break;
        case "advanced":
          endpoint = "/api/upload/advanced-metrics";
          break;
        case "playerdata":
          endpoint = "/api/upload/player-data";
          break;
        default:
          endpoint = "/api/upload/transfers";
      }

      try {
        const response = await axios.post(endpoint, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        console.log("response", response);
        toast.success("File uploaded successfully!");

        // Show validation errors if any
        if (response.data.errors > 0 && response.data.validation_errors) {
          toast.warn(
            `${response.data.errors} records had validation issues. Check console for details.`
          );
          console.warn("Validation errors:", response.data.validation_errors);
        }

        const newFileList = [...recentFiles, file.name].slice(-25);
        localStorage.setItem("recentUploads", JSON.stringify(newFileList));
        setRecentFiles(newFileList);
        setUploadingFile(null);
      } catch (error) {
        toast.error("Upload failed. Please check console.");
        console.error(error);
        setUploadingFile(null);
      }
    },
    [gender, recentFiles, uploadType, season]
  );

  const handleFolderSelect = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      // Get the folder path from the first file
      const folderPathFromFiles =
        event.target.files[0].webkitRelativePath.split("/")[0];
      setFolderPath(folderPathFromFiles);

      // Filter and count CSV files in selection
      const allFiles = Array.from(event.target.files);
      const csvFiles = allFiles.filter((file) =>
        file.name.toLowerCase().endsWith(".csv")
      );

      // Store relevant details about selected files for processing later
      window.selectedCsvFiles = csvFiles;
      setTotalFilesToProcess(csvFiles.length);

      toast.info(
        `Selected folder: ${folderPathFromFiles} (${csvFiles.length} CSV files found)`
      );
    }
  };

  const uploadFolderFiles = useCallback(async () => {
    if (!folderPath) {
      toast.error("Please select a folder!");
      return;
    }

    if (!gender) {
      toast.error("Please select a gender!");
      return;
    }

    if (!window.selectedCsvFiles || window.selectedCsvFiles.length === 0) {
      toast.error("No CSV files found in the selected folder!");
      return;
    }

    setProcessingFolder(true);
    setProcessedFiles(0);
    setProcessingProgress(0);

    try {
      // Process files one by one
      const processedFileNames = [];
      let successCount = 0;
      let errorCount = 0;
      const errorFiles = [];

      // Determine the API endpoint based on upload type
      const endpoint =
        uploadType === "gamelogs"
          ? "/api/upload/gamelogs"
          : "/api/upload/advanced-metrics";

      for (let i = 0; i < window.selectedCsvFiles.length; i++) {
        const file = window.selectedCsvFiles[i];
        setUploadingFile(file);

        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("gender", gender);

          await new Promise((resolve) => setTimeout(resolve, 100)); // Give UI time to update

          const response = await axios.post(endpoint, formData, {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          if (response.data) {
            successCount++;
            processedFileNames.push(file.name);
          }
        } catch (error) {
          errorCount++;
          errorFiles.push(file.name);
          console.error(`ERROR PROCESSING FILE: ${file.name}`);
          console.error(`Error details for ${file.name}:`, error);

          // If server sent a specific error message, log it
          if (error.response && error.response.data) {
            console.error(
              `Server error for ${file.name}:`,
              error.response.data
            );
          }
        } finally {
          setProcessedFiles((prev) => prev + 1);
          setProcessingProgress(
            Math.round(((i + 1) / window.selectedCsvFiles.length) * 100)
          );
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Processed ${successCount} files successfully!`);
      }

      if (errorCount > 0) {
        toast.error(
          `${errorCount} files had issues. Check console for details.`,
          { autoClose: 10000 } // Keep error visible longer
        );
        console.error("Files with errors:", errorFiles);
      }

      // Add the processed files to recent files
      if (processedFileNames.length > 0) {
        const newFileList = [
          ...recentFiles,
          ...processedFileNames.slice(0, 25),
        ].slice(-25);
        localStorage.setItem("recentUploads", JSON.stringify(newFileList));
        setRecentFiles(newFileList);
      }
    } catch (error) {
      toast.error("Folder processing failed. Please check console.");
      console.error("General folder processing error:", error);
    } finally {
      setProcessingFolder(false);
      setUploadingFile(null);
    }
  }, [folderPath, gender, recentFiles, uploadType]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only CSV files are supported!");
      return;
    }
    setSelectedFile(file);
  };

  const handleDeleteFile = (index) => {
    const updatedFiles = recentFiles.filter((_, i) => i !== index);
    localStorage.setItem("recentUploads", JSON.stringify(updatedFiles));
    setRecentFiles(updatedFiles);
  };

  const handleSubmit = async () => {
    if (uploadType === "players" || uploadType === "playerdata") {
      if (!selectedFile) {
        toast.error("Please select a file!");
        return;
      }
      if (!gender) {
        toast.error("Please select a gender!");
        return;
      }

      if (uploadingFile) {
        toast.error("Upload is still in progress. Please wait!");
        return;
      }
      await uploadFile(selectedFile);
    } else {
      // For folder uploads (game logs or advanced metrics)
      await uploadFolderFiles();
    }
  };

  const handleUpdateXFactor = useCallback(async () => {
    try {
      setProcessingXFactor(true);
      const response = await axios.post(
        "/api/upload/xfactor",
        {},
        {
          withCredentials: true,
        }
      );
      toast.success(response.data.message || "XFactor updated!");
    } catch (err) {
      console.error("Failed to update xfactor", err);
      toast.error("XFactor update failed – check console");
    } finally {
      setProcessingXFactor(false);
    }
  }, []);

  // Drag and Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) {
      setDragOver(true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!dragOver) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    if (uploadType !== "players" && uploadType !== "playerdata") {
      toast.info("Drag and drop is only available for file uploads");
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.name.toLowerCase().endsWith(".csv")) {
        toast.error("Only CSV files are supported!");
        return;
      }
      setSelectedFile(file);
    }
  };

  // Get the proper file naming format help text based on upload type
  const getFileNamingHelpText = () => {
    if (uploadType === "gamelogs") {
      return "The folder should contain CSV files named with the format: cbb_player_id___alternate_id.csv";
    } else if (uploadType === "advanced") {
      return "The folder should contain CSV files named with the format: cbb_player_id___alternate_id.csv";
    }
    return "";
  };

  return (
    <PageContainer>
      <div className="game-upload-container">
        <div className="game-upload-left" style={{ position: "relative" }}>
          {(uploadingFile || processingFolder) && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Loader fixed={false} />
              <Typography
                variant="body1"
                style={{
                  marginTop: "20px",
                  fontWeight: "bold",
                }}
              >
                {uploadingFile
                  ? `Processing ${uploadingFile.name}...`
                  : "Processing folder files..."}
              </Typography>

              {processingFolder && (
                <div className="transfer-upload-processing-status">
                  <Typography variant="body2">
                    {processedFiles} of {totalFilesToProcess} files processed
                  </Typography>
                  <div className="transfer-upload-processing-progress">
                    <div
                      className="transfer-upload-processing-progress-bar"
                      style={{ width: `${processingProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="transfer-upload-type-group">
            <Typography variant="h6" gutterBottom>
              Select Upload Type
            </Typography>
            <RadioGroup
              value={uploadType}
              onChange={(e) => {
                setUploadType(e.target.value);
                setSelectedFile(null); // Reset selected file when changing type
                setFolderPath(""); // Reset folder path
                setSeason(""); // Reset season when changing upload type
              }}
              row
            >
              <FormControlLabel
                value="players"
                control={<Radio />}
                label="Player Transfers"
              />
              <FormControlLabel
                value="playerdata"
                control={<Radio />}
                label="Player Data (Dan)"
              />
              <FormControlLabel
                value="gamelogs"
                control={<Radio />}
                label="Game Logs"
              />
              <FormControlLabel
                value="advanced"
                control={<Radio />}
                label="Advanced Metrics"
              />
            </RadioGroup>
          </div>

          {/* Season selector - only show for playerdata uploads */}
          {uploadType === "playerdata" && (
            <div className="game-upload-gender-group">
              <Typography variant="h6" gutterBottom>
                Select Season
              </Typography>
              <RadioGroup
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                row
              >
                <FormControlLabel
                  value=""
                  control={<Radio />}
                  label="Current Season"
                />
                <FormControlLabel
                  value="2024"
                  control={<Radio />}
                  label="2024 Season"
                />
              </RadioGroup>
            </div>
          )}

          {uploadType === "players" || uploadType === "playerdata" ? (
            <div
              className={`game-upload-upload-area ${
                dragOver ? "drag-over" : ""
              }`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Typography variant="h6">
                Drag and Drop files to upload or
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Browse
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileUpload}
                />
              </Button>

              {/* Show the selected file name */}
              {selectedFile && (
                <Typography variant="body2" color="textSecondary">
                  Selected file: {selectedFile.name}
                </Typography>
              )}
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: "8px" }}
              >
                Supported files: CSV
              </Typography>
            </div>
          ) : (
            <div className="transfer-upload-folder-area">
              <Typography variant="h6" gutterBottom>
                Select{" "}
                {uploadType === "gamelogs" ? "Game Logs" : "Advanced Metrics"}{" "}
                Folder
              </Typography>

              <div className="transfer-upload-folder-selector">
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<FolderOpenIcon />}
                  fullWidth
                >
                  Choose Folder
                  <input
                    type="file"
                    hidden
                    ref={folderInputRef}
                    webkitdirectory="true"
                    directory="true"
                    onChange={handleFolderSelect}
                  />
                </Button>
              </div>

              {folderPath && (
                <div className="transfer-upload-selected-folder">
                  <FolderIcon style={{ marginRight: "8px" }} />
                  <Typography>
                    Selected: {folderPath} ({totalFilesToProcess} CSV files)
                  </Typography>
                </div>
              )}

              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: "16px" }}
              >
                {getFileNamingHelpText()}
              </Typography>
            </div>
          )}

          <div className="game-upload-gender-group">
            <Typography variant="h6" gutterBottom>
              Select Gender
            </Typography>
            <RadioGroup
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              row
            >
              <FormControlLabel value="male" control={<Radio />} label="Male" />
              <FormControlLabel
                value="female"
                control={<Radio />}
                label="Female"
              />
            </RadioGroup>
          </div>

          <div className="game-upload-submit-button">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={uploadingFile !== null || processingFolder}
            >
              {uploadType === "players" || uploadType === "playerdata"
                ? "Upload File"
                : "Process Folder"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleUpdateXFactor}
              disabled={
                uploadingFile !== null || processingFolder || processingXFactor
              }
              style={{ marginLeft: "12px" }}
            >
              {processingXFactor ? "Updating XFactor..." : "Update XFactor"}
            </Button>
          </div>
        </div>

        <div className="game-upload-right">
          <Typography
            className="game-upload-recent-uploads-header"
            variant="h6"
          >
            Uploaded Files
          </Typography>
          <Divider />
          <div style={{ marginTop: "16px" }}>
            {recentFiles.length === 0 &&
              !uploadingFile &&
              !processingFolder && (
                <Typography variant="body2" className="game-upload-no-uploads">
                  No recent uploads.
                </Typography>
              )}

            {recentFiles.map((fileName, index) => (
              <div className="game-upload-file-item" key={index}>
                <DescriptionIcon style={{ marginRight: "8px" }} />
                <Typography variant="body1" className="filename">
                  {fileName}
                </Typography>
                <IconButton
                  aria-label="delete file"
                  onClick={() => handleDeleteFile(index)}
                  size="small"
                  disabled={uploadingFile !== null || processingFolder}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

export default TransferUpload;
